import useDebounce from '@/hooks/useDebounce';
import { useFetchHubspotCompanies } from '@/http/queries';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField as MuiTextField,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useField } from 'react-final-form';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { T } from '@/i18n/T';

const NO_MATCH_SENTINEL = { __noMatch: true };

export default function HubspotDomainField({
  input,
  meta,
  required,
  orgDomains = [],
  resetOnNameChange = true,
}) {
  const { t } = useInlineTranslation();
  const { input: nameInput } = useField('name', {
    subscription: { value: true },
  });

  const [searchInput, setSearchInput] = useState(input.value ?? '');
  const prevNameRef = useRef(nameInput.value);

  const debouncedName = useDebounce(nameInput.value ?? '', 350);
  const debouncedSearch = useDebounce(searchInput, 350);
  const activeSearch = searchInput.length > 0 ? debouncedSearch : debouncedName;

  const { data: rawCompanies = [], isFetching } =
    useFetchHubspotCompanies(activeSearch);

  const options = useMemo(() => {
    const filtered = rawCompanies.filter((c) => !orgDomains.includes(c.domain));
    if (activeSearch.length >= 2 && !isFetching && filtered.length === 0) {
      return [NO_MATCH_SENTINEL];
    }
    return filtered;
  }, [rawCompanies, orgDomains, activeSearch, isFetching]);

  useEffect(() => {
    if (!resetOnNameChange) return;
    if (nameInput.value === prevNameRef.current) return;
    prevNameRef.current = nameInput.value;
    setSearchInput('');
    input.onChange('');
  }, [nameInput.value, resetOnNameChange]);

  function handleInputChange(_, newValue, reason) {
    if (reason === 'clear') {
      setSearchInput('');
      input.onChange('');
      return;
    }
    if (reason !== 'input') return;
    setSearchInput(newValue);
    input.onChange(newValue);
  }

  function handleChange(_, selectedOption) {
    if (!selectedOption) {
      setSearchInput('');
      input.onChange('');
      return;
    }
    if (selectedOption.__noMatch) return;
    const domain =
      typeof selectedOption === 'string'
        ? selectedOption
        : selectedOption.domain;
    setSearchInput(domain);
    input.onChange(domain);
  }

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={isFetching}
      inputValue={searchInput}
      onInputChange={handleInputChange}
      onChange={handleChange}
      filterOptions={(x) => x}
      getOptionLabel={(opt) => {
        if (typeof opt === 'string') return opt;
        if (opt.__noMatch) return '';
        return opt.domain || '';
      }}
      isOptionEqualToValue={(opt, val) => {
        if (opt.__noMatch || val?.__noMatch) return false;
        const a = typeof opt === 'string' ? opt : opt.domain;
        const b = typeof val === 'string' ? val : val.domain;
        return a === b;
      }}
      loadingText={t('loading')}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        if (option.__noMatch) {
          return (
            <Box
              component='li'
              key='no-match'
              {...optionProps}
              sx={{
                pointerEvents: 'none',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
              aria-disabled
            >
              <T k='noMatchingDomains' />
            </Box>
          );
        }
        return (
          <Box component='li' key={key ?? option.domain} {...optionProps}>
            <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
              <Box component='span' sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                {option.name}
              </Box>
              <Box
                component='span'
                sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}
              >
                {option.domain}
              </Box>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={<T k='domain' />}
          placeholder={t('selectDomain')}
          error={(meta.touched || meta.submitFailed) && !!meta.error}
          helperText={(meta.touched || meta.submitFailed) && meta.error}
          required={required}
          autoComplete='off'
          onBlur={input.onBlur}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      fullWidth
    />
  );
}
